/**
 * OrgSelectorPage.tsx — Post-login org selection for multi-org users
 *
 * Displayed after OIDC callback when the user has access to multiple orgs.
 * Shows a card list of available orgs with role badges.
 *
 * @since 2026-06-19 (SaaS conversion — Phase 8)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgContext, type OrgInfo } from '../contexts/OrgContext';

export default function OrgSelectorPage(): React.JSX.Element {
  const { availableOrgs, selectOrg, isPlatformAdmin, loading, error } = useOrgContext();
  const navigate = useNavigate();

  function handleSelectOrg(org: OrgInfo): void {
    selectOrg(org.orgId);
    navigate(`/org/${org.orgId}/overview`);
  }

  function handlePlatformAdmin(): void {
    navigate('/platform/dashboard');
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
          <p className="mt-4 text-sm text-slate-400">Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-8 text-center">
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (availableOrgs.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="max-w-md rounded-2xl bg-slate-900 border border-white/10 p-10 text-center">
          <h2 className="text-xl font-semibold text-white">No Billing Access</h2>
          <p className="mt-3 text-sm text-slate-400">
            Your account does not have billing access to any organization.
            Contact your administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.16),_transparent_28%),linear-gradient(180deg,_#08111f_0%,_#0f172a_100%)]">
      <div className="w-full max-w-2xl px-6">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-cyan-300/80">
            Rule42 Billing
          </p>
          <h1 className="mt-3 text-2xl font-semibold text-white">
            Select Organization
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            Choose which organization to manage billing for.
          </p>
        </div>

        <div className="mt-8 grid gap-4">
          {availableOrgs.map((org) => (
            <button
              key={org.orgId}
              onClick={() => handleSelectOrg(org)}
              className="group flex items-center justify-between rounded-2xl border border-white/10 bg-slate-900/60 px-6 py-5 text-left transition hover:border-cyan-400/30 hover:bg-slate-900/80"
            >
              <div>
                <h3 className="text-lg font-semibold text-white group-hover:text-cyan-300">
                  {org.orgName}
                </h3>
                <p className="mt-1 text-sm text-slate-400">{org.slug}</p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    org.role === 'admin'
                      ? 'bg-cyan-400/15 text-cyan-300'
                      : 'bg-slate-700 text-slate-300'
                  }`}
                >
                  {org.role}
                </span>
                <span
                  className={`h-2 w-2 rounded-full ${
                    org.status === 'active' ? 'bg-green-400' : 'bg-yellow-400'
                  }`}
                />
              </div>
            </button>
          ))}
        </div>

        {isPlatformAdmin && (
          <div className="mt-8 text-center">
            <button
              onClick={handlePlatformAdmin}
              className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-6 py-2.5 text-sm font-medium text-cyan-300 transition hover:bg-cyan-400/20"
            >
              Open Platform Admin
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
